import { useCallback, useEffect, useState } from "react";
import { Download, File as FileIcon, Folder, FolderPlus, Pencil, Trash2, Upload } from "lucide-react";
import { api, del, patch, post, postForm, put } from "../../api";
import { formatBytes } from "../../components/common";
import { EmptyState, useActionDialog } from "../../components/ui";
import type { Role } from "../../types";

interface FileEntry { path: string; name: string; directory: boolean; sizeBytes: number; modifiedAt: number }
interface FileDocument { path: string; content: string; sizeBytes: number; modifiedAt: number }

export function FileManager({ serverId, role }: { serverId: string; role: Role }) {
  const { confirm: confirmAction, prompt: promptAction, host: dialogHost } = useActionDialog();
  const [path, setPath] = useState("");
  const [items, setItems] = useState<FileEntry[]>([]);
  const [selected, setSelected] = useState<FileDocument | null>(null);
  const [upload, setUpload] = useState<File | null>(null);
  const [newDirectory, setNewDirectory] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const canBasic = role !== "viewer";
  const canAdvanced = role === "owner" || role === "administrator";
  const load = useCallback(async () => {
    setError("");
    try { const next = await api<FileEntry[]>(`/servers/${serverId}/files?path=${encodeURIComponent(path)}`); setItems(Array.isArray(next) ? next : []); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível listar os arquivos."); }
  }, [serverId, path]);
  useEffect(() => { void load(); }, [load]);
  const child = (name: string) => path ? `${path}/${name}` : name;
  const parent = () => setPath(path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "");
  const uploadFile = async () => {
    if (!upload) return;
    setBusy(true); setError("");
    try { const form = new FormData(); form.append("path", child(upload.name)); form.append("file", upload); await postForm(`/servers/${serverId}/files/upload`, form); setUpload(null); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível enviar o arquivo."); }
    finally { setBusy(false); }
  };
  const mkdir = async () => {
    if (!newDirectory) return;
    setBusy(true); setError("");
    try { await post(`/servers/${serverId}/files/directory`, { path: child(newDirectory) }); setNewDirectory(""); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível criar a pasta."); }
    finally { setBusy(false); }
  };
  const openEditor = async (item: FileEntry) => {
    setError("");
    try { setSelected(await api<FileDocument>(`/servers/${serverId}/files/edit?path=${encodeURIComponent(item.path)}`)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Este arquivo não pode ser editado como texto."); }
  };
  const save = async () => {
    if (!selected) return;
    setBusy(true); setError("");
    try { await put(`/servers/${serverId}/files/edit`, { path: selected.path, content: selected.content }); setSelected(null); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível salvar o arquivo."); }
    finally { setBusy(false); }
  };
  const move = async (item: FileEntry) => {
    const target = await promptAction({ title: "Mover ou renomear", description: "Informe o novo caminho dentro dos arquivos deste servidor.", confirmLabel: "Salvar caminho", input: { label: "Novo caminho", defaultValue: item.path } });
    if (!target || target === item.path) return;
    setBusy(true);
    try { await patch(`/servers/${serverId}/files`, { path: item.path, targetPath: target }); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível mover o item."); }
    finally { setBusy(false); }
  };
  const remove = async (item: FileEntry) => {
    if (!await confirmAction({ title: `Excluir ${item.name}?`, description: "O item será removido dos arquivos deste servidor.", confirmLabel: "Excluir item", tone: "danger" })) return;
    setBusy(true);
    try { await del(`/servers/${serverId}/files`, { path: item.path }); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível excluir o item."); }
    finally { setBusy(false); }
  };
  const extract = async (item: FileEntry) => {
    setBusy(true);
    try { await post(`/servers/${serverId}/files/extract`, { path: item.path, targetPath: path }); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível extrair o ZIP."); }
    finally { setBusy(false); }
  };

  return <>
    <section className="panel file-manager">
      <div className="panel-head"><div><span className="eyebrow">AVANÇADO</span><h2>Arquivos</h2></div><Folder size={20}/></div>
      <div className="notice warning">Arquivo avançado. Alterações incorretas podem impedir o servidor de iniciar.</div>
      <div className="file-path"><button className="secondary small" disabled={!path || busy} onClick={parent}>← Voltar</button><code>/{path}</code></div>
      {canBasic && <div className="file-tools">
        <label className="secondary small"><Upload size={15}/>Selecionar arquivo<input type="file" hidden onChange={(event) => setUpload(event.target.files?.[0] ?? null)}/></label>
        <button className="primary small" disabled={!upload || busy} onClick={() => void uploadFile()}>{upload ? `Enviar ${upload.name}` : "Enviar"}</button>
        <input aria-label="Nova pasta" value={newDirectory} onChange={(event) => setNewDirectory(event.target.value)} placeholder="Nova pasta"/>
        <button className="secondary small" disabled={!newDirectory || busy} onClick={() => void mkdir()}><FolderPlus size={15}/>Criar pasta</button>
      </div>}
      <div className="file-list">
        {items.map((item) => <div className="file-row" key={item.path}>
          <button className="file-name" onClick={() => item.directory ? setPath(item.path) : undefined}>{item.directory ? <Folder size={17}/> : <FileIcon size={17}/>}<span><b>{item.name}</b><small>{item.directory ? "Pasta" : formatBytes(item.sizeBytes)}</small></span></button>
          <div className="inline-actions">
            {!item.directory && <a className="icon-btn" aria-label={`Baixar ${item.name}`} href={`/api/v1/servers/${serverId}/files/download?path=${encodeURIComponent(item.path)}`}><Download size={15}/></a>}
            {canAdvanced && !item.directory && <button className="icon-btn" aria-label={`Editar ${item.name}`} onClick={() => void openEditor(item)}><Pencil size={15}/></button>}
            {canBasic && <button className="text-button" onClick={() => void move(item)}>Mover/renomear</button>}
            {canAdvanced && !item.directory && item.name.toLowerCase().endsWith(".zip") && <button className="text-button" onClick={() => void extract(item)}>Extrair</button>}
            {canAdvanced && <button className="icon-btn" aria-label={`Excluir ${item.name}`} onClick={() => void remove(item)}><Trash2 size={15}/></button>}
          </div>
        </div>)}
        {!items.length && <EmptyState kind="worlds" compact title="Esta pasta está vazia" description="Envie um arquivo ou crie uma pasta para começar."/>}
      </div>
      {selected && <div className="file-editor"><h3>Editar {selected.path}</h3><textarea aria-label="Conteúdo do arquivo" value={selected.content} onChange={(event) => setSelected({ ...selected, content: event.target.value })} rows={16}/><div className="inline-actions"><button className="primary small" disabled={busy} onClick={() => void save()}>Salvar com versão anterior</button><button className="secondary small" onClick={() => setSelected(null)}>Cancelar</button></div></div>}
      {error && <div className="notice error">{error}</div>}
    </section>
    {dialogHost}
  </>;
}
